import React, { useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Grid,
    Divider,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Download as DownloadIcon,
    AttachFile as AttachFileIcon,
    Visibility as VisibilityIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import { useFetch } from '../../../../hooks/useFecth';
import useErrors from '../../../../hooks/useErrors';
import server from '../../../../services/server';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useConfirm } from 'material-ui-confirm';
import FileUpload from '../../../../components/FileUpload';

const categories = [
    { value: 'contrato', label: 'Contrato' },
    { value: 'documento', label: 'Documento' },
    { value: 'anexo', label: 'Anexo' },
    { value: 'comprovante', label: 'Comprovante' },
    { value: 'outros', label: 'Outros' },
];

// Modal de detalhes do arquivo
function DialogFileDetails({ file, open, onClose }) {
    if (!file) return null;

    const getCategoryLabel = (category) => {
        const categoryObj = categories.find(cat => cat.value === category);
        return categoryObj ? categoryObj.label : category;
    };

    const getCategoryColor = (category) => {
        const colors = {
            contrato: 'primary',
            documento: 'secondary',
            anexo: 'info',
            comprovante: 'success',
            outros: 'default',
        };
        return colors[category] || 'default';
    };

    const handleDownload = () => {
        window.open(file.fileUrl, '_blank');
    };

    const isImage = file.fileUrl?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
    const isPDF = file.fileUrl?.match(/\.pdf$/i);

    return (
        <Dialog  open={open} onClose={onClose} maxWidth="xl" fullWidth>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <DialogTitle>Detalhes do Arquivo</DialogTitle>
                <IconButton onClick={onClose} sx={{ mr: 2 }}>
                    <CloseIcon />
                </IconButton>
            </Stack>

            <Divider />

            <DialogContent sx={{ pb: 1 }}>
                <Stack spacing={2} sx={{ py: 2 }}>
                    
                    {/* Card de Informações Principais */}
                    <Card>
                        <CardContent>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Stack spacing={1}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Nome do Arquivo
                                        </Typography>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <AttachFileIcon fontSize="small" color="action" />
                                            <Typography variant="body1" sx={{ fontWeight: 500, wordBreak: 'break-all' }}>
                                                {file.fileName}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </Grid>
                             

                                <Grid item xs={12} md={6}>
                                    <Stack spacing={1}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Categoria
                                        </Typography>
                                        <Box>
                                            <Chip
                                                label={getCategoryLabel(file.category || 'outros')}
                                                color={getCategoryColor(file.category || 'outros')}
                                                size="medium"
                                            />
                                        </Box>
                                    </Stack>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Stack spacing={1}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Status
                                        </Typography>
                                        <Box>
                                            <Chip
                                                label={file.status ? 'Ativo' : 'Inativo'}
                                                color={file.status ? 'success' : 'default'}
                                                size="medium"
                                            />
                                        </Box>
                                    </Stack>
                                </Grid>

                                {file.description && (
                                    <Grid item xs={12}>
                                        <Stack spacing={1}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Descrição
                                            </Typography>
                                            <Typography variant="body2">
                                                {file.description}
                                            </Typography>
                                        </Stack>
                                    </Grid>
                                )}
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Card de Preview do Arquivo */}
                    {(isImage || isPDF) && (
                        <Card>
                            <CardContent>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Preview
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        mt: 2,
                                        p: 2,
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: 1,
                                        minHeight: 200,
                                    }}
                                >
                                    {isImage ? (
                                        <Box
                                            component="img"
                                            src={file.fileUrl}
                                            alt={file.fileName}
                                            sx={{
                                                maxWidth: '100%',
                                                maxHeight: 400,
                                                objectFit: 'contain',
                                                borderRadius: 1,
                                            }}
                                        />
                                    ) : isPDF ? (
                                        <iframe
                                            src={file.fileUrl}
                                            style={{
                                                width: '100%',
                                                height: 400,
                                                border: 'none',
                                                borderRadius: 4,
                                            }}
                                            title={file.fileName}
                                        />
                                    ) : null}
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    {/* Card de Link do Arquivo */}
                    <Card>
                        <CardContent>
                            <Stack spacing={1}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Link do Arquivo
                                </Typography>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        backgroundColor: '#f9f9f9',
                                        borderRadius: 1,
                                    }}
                                >
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            wordBreak: 'break-all', 
                                            fontFamily: 'monospace',
                                            fontSize: '0.85rem',
                                            color: 'text.secondary',
                                        }}
                                    >
                                        {file.fileUrl}
                                    </Typography>
                                </Paper>
                            </Stack>
                        </CardContent>
                    </Card>

                    {/* Card de Informações do Sistema */}
                    <Card>
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Stack spacing={1}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Data de Criação
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {format(new Date(file.createdAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                                        </Typography>
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Stack spacing={1}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Última Atualização
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {format(new Date(file.updatedAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                                        </Typography>
                                    </Stack>
                                </Grid>
                                {file.uploadedBy && (
                                    <Grid item xs={12}>
                                        <Stack spacing={1}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Enviado por
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                Usuário ID: {file.uploadedBy}
                                            </Typography>
                                        </Stack>
                                    </Grid>
                                )}
                            </Grid>
                        </CardContent>
                    </Card>

                </Stack>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} variant="outlined">
                    Fechar
                </Button>
                <Button 
                    variant="contained" 
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                >
                    Baixar Arquivo
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function DialogUploadFile({ contract, mutate }) {
    const [open, setOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileBase64, setFileBase64] = useState(null);
    const [fileCategory, setFileCategory] = useState('');
    const [fileDescription, setFileDescription] = useState('');
    const { enqueueSnackbar, setErrors } = useErrors();

    const { handleSubmit, setFieldValue, errors, isSubmitting } = useFormik({
        initialValues: {
            fileName: '',
            fileUrl: '',
            category: '',
            description: '',
        },
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);
                
                const payload = {
                    fileName: values.fileName,
                    fileUrl: values.fileUrl,
                    category: fileCategory || values.category,
                    description: fileDescription || values.description,
                    fileBase64: fileBase64
                };
                
                await server.post(`contract/${contract?.id}/files`, payload);
                enqueueSnackbar(`Arquivo adicionado com sucesso.`);
                await mutate();
                setSubmitting(false);
                setOpen(false);
                handleReset();
            } catch (error) {
                console.error('Error uploading file:', error);
                setSubmitting(false);
                setErrors(error);
            }
        },
    });

    const handleReset = () => {
        setSelectedFile(null);
        setFileBase64(null);
        setFileCategory('');
        setFileDescription('');
        setFieldValue('fileName', '');
        setFieldValue('fileUrl', '');
        setFieldValue('category', '');
        setFieldValue('description', '');
    };

    const handleFileSelect = (fileData) => {
        setSelectedFile(fileData.file);
        setFileBase64(fileData.fileBase64);
        setFileCategory(fileData.category || '');
        setFileDescription(fileData.description || '');
        setFieldValue('fileName', fileData.fileName);
    };

    const handleCategoryChange = (value) => {
        setFileCategory(value);
    };

    const handleDescriptionChange = (value) => {
        setFileDescription(value);
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        setFileBase64(null);
        setFileCategory('');
        setFileDescription('');
        setFieldValue('fileName', '');
    };

    return (
        <>
            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpen(true)}
                sx={{ mb: 2 }}
            >
                Adicionar Arquivo
            </Button>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Adicionar Arquivo</DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent>
                        <FileUpload
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                            categories={categories}
                            showCategory={true}
                            showDescription={true}
                            onFileSelect={handleFileSelect}
                            onRemove={handleRemoveFile}
                            onCategoryChange={handleCategoryChange}
                            onDescriptionChange={handleDescriptionChange}
                            selectedFile={selectedFile}
                            fileBase64={fileBase64}
                            category={fileCategory}
                            description={fileDescription}
                            inputId="contract-file-input"
                            disabled={isSubmitting}
                            required={true}
                            error={!!errors.fileName}
                            helperText={errors.fileName}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => {
                            setOpen(false);
                            handleReset();
                        }}>
                            Cancelar
                        </Button>
                        <Button type="submit" variant="contained" disabled={isSubmitting || !selectedFile}>
                            Adicionar
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </>
    );
}

function ContractFiles({ contract, mutate }) {
    const { enqueueSnackbar, setErrors } = useErrors();
    const [selectedFile, setSelectedFile] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);

    const confirm = useConfirm();
    // Ajustar para usar contract diretamente ou buscar o ID do contrato
    const contractId = contract?.id || contract?.Contract?.id;
    
    const { data: files, mutate: mutateFiles } = useFetch(`contract/${contractId}/files`, {
        enabled: !!contractId,
    });

    const handleDeleteFile = async (fileId) => {
        try {
            await confirm({
                title: 'Remover Arquivo',
                description: 'Tem certeza que deseja remover este arquivo? Esta ação não pode ser desfeita.',
                confirmationText: 'Confirmo',
            });
            await server.delete(`contract/${contractId}/files/${fileId}`);
            enqueueSnackbar(`Arquivo removido com sucesso.`);
            await mutateFiles();
        } catch (error) {
            setErrors(error);
        }
    };

    const handleDownloadFile = (fileUrl) => {
        window.open(fileUrl, '_blank');
    };

    const handleViewDetails = (file) => {
        setSelectedFile(file);
        setDetailsOpen(true);
    };

    const getCategoryLabel = (category) => {
        const categoryObj = categories.find(cat => cat.value === category);
        return categoryObj ? categoryObj.label : category;
    };

    const getCategoryColor = (category) => {
        const colors = {
            contrato: 'primary',
            documento: 'secondary',
            anexo: 'info',
            comprovante: 'success',
            outros: 'default',
        };
        return colors[category] || 'default';
    };

    if (!contractId) {
        return (
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Arquivos do Contrato
                </Typography>
                <Typography color="text.secondary">
                    Nenhum contrato encontrado.
                </Typography>
            </Paper>
        );
    }

    return (
        <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
                Arquivos do Contrato
            </Typography>

            <DialogUploadFile contract={{ id: contractId }} mutate={mutateFiles} />

            {files?.data?.length === 0 ? (
                <Card>
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                        <AttachFileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            Nenhum arquivo encontrado
                        </Typography>
                        <Typography color="text.secondary">
                            Adicione arquivos relacionados ao contrato.
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {/* <TableCell>Nome do Arquivo</TableCell> */}
                                <TableCell>Categoria</TableCell>
                                <TableCell>Descrição</TableCell>
                                <TableCell>Data de Criação</TableCell>
                                <TableCell align="center">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {files?.map((file) => (
                                <TableRow key={file.id}>
                                    <TableCell>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <AttachFileIcon color="action" />
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {file.description}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getCategoryLabel(file.category || 'outros')}
                                            color={getCategoryColor(file.category)}
                                            size="small"
                                        />
                                    </TableCell>
                                    {/* <TableCell>
                                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }}>
                                            {file.description || '--'}
                                        </Typography>
                                    </TableCell> */}
                                    <TableCell>
                                        <Typography variant="body2">
                                            {format(new Date(file.createdAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Stack direction="row" spacing={1} justifyContent="center">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleViewDetails(file)}
                                                color="info"
                                                title="Ver detalhes"
                                            >
                                                <VisibilityIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDownloadFile(file.fileUrl)}
                                                color="primary"
                                                title="Baixar arquivo"
                                            >
                                                <DownloadIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteFile(file.id)}
                                                color="error"
                                                title="Remover arquivo"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Modal de detalhes */}
            <DialogFileDetails 
                file={selectedFile} 
                open={detailsOpen} 
                onClose={() => setDetailsOpen(false)} 
            />
        </Paper>
    );
}


export default ContractFiles; 